import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from "firebase/firestore";

interface Chapter {
  id: string;
  class: string;
  chapterName: string;
}

const classOptions = ["IX", "X", "XI", "XII"];

const TopicManagement = () => {
  // Chapter state
  const [chapterName, setChapterName] = useState("");
  const [chapterClass, setChapterClass] = useState("");
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [chapterEditId, setChapterEditId] = useState<string | null>(null);
  const [editChapterName, setEditChapterName] = useState("");
  const [editChapterClass, setEditChapterClass] = useState("");

  // UI state
  const [showChapterForm, setShowChapterForm] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchChapters();
  }, []);

const fetchChapters = async () => {
  try {
    const q = query(collection(db, "chapters"), orderBy("class"));
    const querySnapshot = await getDocs(q);
    const data = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as { class: string; chapterName: string })
    }));
    setChapters(data);
  } catch (err) {
    setError("Failed to fetch chapters.");
  }
};

// (Topics removed)

  // Chapter CRUD
const handleChapterSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setSuccess("");
  if (!chapterClass || !chapterName) {
    setError("Please select a class and enter a chapter name.");
    return;
  }
  try {
    await addDoc(collection(db, "chapters"), {
      class: chapterClass,
      chapterName,
      createdAt: new Date(),
    });
    setSuccess("Chapter added successfully!");
    setChapterClass("");
    setChapterName("");
    setShowChapterForm(false);
    fetchChapters();
  } catch (err) {
    setError("Failed to add chapter.");
  }
};

const handleChapterDelete = async (id: string) => {
  if (!window.confirm("Are you sure you want to delete this chapter?")) return;
  try {
    await deleteDoc(doc(db, "chapters", id));
    fetchChapters();
  } catch (err) {
    setError("Failed to delete chapter.");
  }
};

const handleChapterEdit = (ch: Chapter) => {
  setChapterEditId(ch.id);
  setEditChapterClass(ch.class);
  setEditChapterName(ch.chapterName);
  setShowChapterForm(false);
  setError("");
  setSuccess("");
};
const handleChapterEditSave = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!editChapterClass || !editChapterName) {
    setError("Please select a class and enter a chapter name.");
    return;
  }
  try {
    await updateDoc(doc(db, "chapters", chapterEditId!), {
      class: editChapterClass,
      chapterName: editChapterName,
    });
    setChapterEditId(null);
    setEditChapterClass("");
    setEditChapterName("");
    setSuccess("Chapter updated successfully!");
    fetchChapters();
  } catch (err) {
    setError("Failed to update chapter.");
  }
};
const handleChapterEditCancel = () => {
  setChapterEditId(null);
  setEditChapterClass("");
  setEditChapterName("");
  setError("");
  setSuccess("");
};

// (Topic CRUD removed)

return (
  <div className="max-w-3xl mx-auto bg-white p-6 rounded shadow mt-8">
    <h2 className="text-xl font-bold mb-4">Chapter Management</h2>

    {/* Success/Error Messages */}
    {success && <div className="mb-2 p-2 bg-green-100 text-green-700 rounded">{success}</div>}
    {error && <div className="mb-2 p-2 bg-red-100 text-red-700 rounded">{error}</div>}

    {/* Chapter Management Section */}
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-lg">Chapters</h3>
        <button
          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-700"
          onClick={() => setShowChapterForm((prev) => !prev)}
        >
          {showChapterForm ? "Hide" : "Add Chapter"}
        </button>
      </div>
      {showChapterForm && (
        <form onSubmit={handleChapterSubmit} className="flex flex-col md:flex-row gap-2 mb-4">
          <select
            className="border px-2 py-1 rounded"
            value={chapterClass}
            onChange={e => setChapterClass(e.target.value)}
            required
          >
            <option value="">Select Class</option>
            {classOptions.map((cls) => (
              <option key={cls} value={cls}>{`Class ${cls}`}</option>
            ))}
          </select>
          <input
            type="text"
            className="border px-2 py-1 rounded"
            placeholder="Chapter Name"
            value={chapterName}
            onChange={e => setChapterName(e.target.value)}
            required
          />
          <button
            type="submit"
            className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
          >
            Add
          </button>
        </form>
      )}
      {/* Chapter List categorized by Class */}
      {classOptions.map((cls) => {
        const rows = chapters.filter((ch) => ch.class === cls);
        return (
          <div key={cls} className="mb-6">
            <h4 className="font-semibold text-blue-700 mb-2">Class {cls}</h4>
            <table className="min-w-full border text-sm mb-2">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-4 py-2">Chapter Name</th>
                  <th className="border px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-center text-gray-500">No chapters for Class {cls}.</td>
                  </tr>
                ) : (
                  rows.map((ch) => (
                    <tr key={ch.id}>
                      {chapterEditId === ch.id ? (
                        <>
                          <td className="border px-4 py-2">
                            <input
                              type="text"
                              className="border px-2 py-1 rounded w-full"
                              value={editChapterName}
                              onChange={e => setEditChapterName(e.target.value)}
                              required
                            />
                          </td>
                          <td className="border px-4 py-2">
                            <button className="bg-green-600 text-white px-2 py-1 rounded mr-2 hover:bg-green-700" onClick={handleChapterEditSave}>Save</button>
                            <button className="bg-gray-400 text-white px-2 py-1 rounded hover:bg-gray-500" onClick={handleChapterEditCancel}>Cancel</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="border px-4 py-2">{ch.chapterName}</td>
                          <td className="border px-4 py-2">
                            <button className="bg-yellow-500 text-white px-2 py-1 rounded mr-2 hover:bg-yellow-600" onClick={() => handleChapterEdit(ch)}>Edit</button>
                            <button className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700" onClick={() => handleChapterDelete(ch.id)}>Delete</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  </div>
);

}

export default TopicManagement;
